//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import Redux, { Action } from 'redux';
import _ from 'underscore';

import * as collectionsApi from '@apis/collections/collectionsApi';
import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { CollectionType } from '@apis/collections/CollectionType';
import { ICollectionResponse } from '@apis/collections/ICollectionResponse';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import * as instagramSettingsApi from '@apis/instagram-settings/instagramSettingsApi';
import { IGetInstagramSettingsResponse } from '@apis/instagram-settings/instagramSettingsApiModel';
import { FeatureNames } from '@bvengineering/curalate-features';
import { ITEMS_TO_RENDER } from '@galleries/components/modals/unmoderated-content/UnmoderatedContentModal';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { createAction } from '@utils/actions';
import {
  getCollectionFiltersConverter,
  getContentLibraryApiService,
  getStreamsDrawerActions,
} from '@react-bridge/wrappers/ng-service-provider';
import * as shareApi from '@share/api/shareApi';
import { IShareRejectionReason } from '@share/models/shareStateModel';
import { IStreamsDrawerState } from '@streams-drawer/model/AppState';
import { IStream } from '@streams-drawer/model/IStream';
import { StreamsDrawerActionNames } from '@streams-drawer/model/StreamsDrawerActionNames';
import {
  nameToStreamType,
  StreamsDrawerStreamType,
} from '@streams-drawer/streams-drawer-constants';
import { assetFeedsModalActions } from '@streams/actions/assetFeedsModalActions';
import { setTrackingSourceTimeout } from '@streams/actions/streamCreationActions';
import { MEDIA_SOURCE } from '@streams/constants/assetFeedsModalConstants';
import { toLabelsString } from '@utils/labelUtils';
import { crl8 } from 'app/curalate.globals';
import {
  setContentDiscoveryFlag,
  setRecommendationsStreams,
} from 'app/streams-drawer/actions/streams-drawer-actions';
import { IConfiguration } from '@apis/tagged-by-curalate-automation/automationApiModels';
import * as taggedByBVAutomationApi from '@apis/tagged-by-curalate-automation/automationAPI';
import ReelId from '@models/ReelId';
import { createThunk } from '@redux/createThunk';
import { IContentItemDetails } from 'app/bulk-actions/model/AppState';

interface IOpenStreamsDrawerAction extends Action {
  type: StreamsDrawerActionNames.OPEN_STREAMS_DRAWER;
}

interface IUpdateItemsInPlaceAction extends Action {
  type: StreamsDrawerActionNames.UPDATE_ITEMS_IN_PLACE;
  items: IContentLibraryItem[];
  totalItemCount: number;
}

interface IOpenRejectionReasonsModal extends Action {
  type: StreamsDrawerActionNames.OPEN_REJECTION_REASONS_MODAL;
  cuid: Cuid;
}

interface ICloseRejectionReasonsModal extends Action {
  type: StreamsDrawerActionNames.CLOSE_REJECTION_REASONS_MODAL;
}

interface IReceiveBrandInstagramFailedAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_FAILED;
}

interface IReceiveBrandInstagramSucceededAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_SUCCEEDED;
  brandInstagramId: string;
  brandInstagramUsername: string;
}

interface IReceiveClientRejectionReasonsFailedAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_FAILED;
}

interface IReceiveClientRejectionReasonsSucceededAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_SUCCEEDED;
  clientRejectionReasons: IShareRejectionReason[];
}

interface IRequestClientRejectionReasonsAction extends Action {
  type: StreamsDrawerActionNames.REQUEST_CLIENT_REJECTION_REASONS;
}

interface IRequestBrandInstagramAction extends Action {
  type: StreamsDrawerActionNames.REQUEST_BRAND_INSTAGRAM;
}

interface IReceiveBrandFacebookFailedAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_FAILED;
}

interface IReceiveBrandFacebookSucceededAction extends Action {
  type: StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_SUCCEEDED;
  pageName: string;
}

interface IRequestBrandFacebookAction extends Action {
  type: StreamsDrawerActionNames.REQUEST_BRAND_FACEBOOK;
}

interface ISetTaggedByCuralatePendingCountAction extends Action {
  type: StreamsDrawerActionNames.SET_TAGGED_BY_CURALATE_PENDING_COUNT;
  taggedByCuralatePendingCount: number;
  taggedByCuralatePendingPreviewItems: IContentLibraryItem[];
}

interface ISetConnectPendingCountAction extends Action {
  type: StreamsDrawerActionNames.SET_CONNECT_PENDING_COUNT;
  connectPendingCount: number;
}

export interface IContentLibraryExtendedItem extends IContentLibraryItem {
  itemDetails?: IContentItemDetails;
}

export interface ISelectStreamAction extends Action {
  type: StreamsDrawerActionNames.SELECT_STREAM;
  stream: IStream;
}

export interface ISetStreamsAction extends Action {
  type: StreamsDrawerActionNames.SET_STREAMS;
  streams: ICollectionResponse[];
}

export interface ISetTrackingSourceTimeout extends Action {
  type: StreamsDrawerActionNames.SET_TRACKING_SOURCE_TIMEOUT;
  trackingTimeout: number;
}

interface IReceiveclientTaggedByBvAutomationResponseFailedAction
  extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_FAILED;
}

interface IReceiveclientTaggedByBvAutomationResponseSucceededAction
  extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_SUCCEEDED;
  taggedByBvAutomationConfiguration: IConfiguration[];
}

interface IReceiveclientTaggedByBvActiveAutomationResponseSucceededAction
  extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_AUTOMATION_CONFIGURATION_SUCCEEDED;
  taggedByBvAutomationActiveConfiguration: IConfiguration;
}

interface IReceiveclientTaggedByBvNoActiveAutomationData extends Action {
  type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_NO_AUTOMATION_CONFIGURATION;
}

interface IOpenAutoApprovalEntryModal extends Action {
  type: StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_ENTRY_MODAL;
}

interface ICloseAutoApprovalEntryModal extends Action {
  type: StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_ENTRY_MODAL;
}

interface IOpenAutoApprovalModal extends Action {
  type: StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_MODAL;
}

interface ICloseAutoApprovalModal extends Action {
  type: StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_MODAL;
}

interface IDeleteTaggedByBvAutomationSuccess extends Action {
  type: StreamsDrawerActionNames.DELETE_TBB_AUTOMATION_CONFIGURATION_SUCCEEDED;
}

interface IDeleteTaggedByBvAutomationFailed extends Action {
  type: StreamsDrawerActionNames.DELETE_TBB_AUTOMATION_CONFIGURATION_FAILED;
}

export type StreamDrawerActionType =
  | IOpenStreamsDrawerAction
  | IOpenRejectionReasonsModal
  | ICloseRejectionReasonsModal
  | IReceiveBrandInstagramFailedAction
  | IReceiveBrandInstagramSucceededAction
  | IRequestClientRejectionReasonsAction
  | IReceiveClientRejectionReasonsFailedAction
  | IReceiveClientRejectionReasonsSucceededAction
  | IRequestBrandInstagramAction
  | IReceiveBrandFacebookFailedAction
  | IReceiveBrandFacebookSucceededAction
  | IRequestBrandFacebookAction
  | IReceiveclientTaggedByBvAutomationResponseFailedAction
  | IReceiveclientTaggedByBvAutomationResponseSucceededAction
  | IReceiveclientTaggedByBvActiveAutomationResponseSucceededAction
  | ISelectStreamAction
  | ISetTaggedByCuralatePendingCountAction
  | ISetConnectPendingCountAction
  | IOpenAutoApprovalEntryModal
  | ICloseAutoApprovalEntryModal
  | IOpenAutoApprovalModal
  | ICloseAutoApprovalModal
  | IDeleteTaggedByBvAutomationSuccess
  | IDeleteTaggedByBvAutomationFailed;

const getInstagramSettings = (
  instagramSettings: IGetInstagramSettingsResponse,
) => {
  let brandInstagramId;
  let brandInstagramUsername;

  if (_(instagramSettings.accounts).size() > 0) {
    brandInstagramUsername = instagramSettings.accounts[0]?.username;
    brandInstagramId = instagramSettings.accounts[0]?.accountId;
  }

  return {
    brandInstagramId,
    brandInstagramUsername,
  };
};

export type StreamDrawerActions = typeof actions;

export const actions = {
  openRejectionReasonsModal:
    (cuid: Cuid) => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<IOpenRejectionReasonsModal>({
        type: StreamsDrawerActionNames.OPEN_REJECTION_REASONS_MODAL,
        cuid,
      });
    },

  closeRejectionReasonsModal:
    () => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<ICloseRejectionReasonsModal>({
        type: StreamsDrawerActionNames.CLOSE_REJECTION_REASONS_MODAL,
      });
    },

  fetchTaggedByCuralatePendingCount:
    () => async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      const state: IStreamsDrawerState = getState().streams.streamsDrawer;
      const clientId = ClientId.fromString(getState().session.client.id);
      const { recommendationsStreams } = state;

      const collectionFiltersConverter = getCollectionFiltersConverter();
      const contentLibraryApiService = getContentLibraryApiService();

      // If the user has a Moderation Services "Tagged by Curalate"
      // stream, fetch the number of pending items so we can show an
      // indicator in the UI.
      const taggedByCuralateStream = recommendationsStreams.find(
        (recStream) =>
          nameToStreamType(recStream) ===
          StreamsDrawerStreamType.TAGGED_BY_CURALTE,
      );

      if (taggedByCuralateStream) {
        const taggedByCuralateFilters =
          collectionFiltersConverter.collectionToFilters(
            taggedByCuralateStream,
          );
        const taggedByCuralateResp =
          await contentLibraryApiService.getContentItemsAsync({
            ...taggedByCuralateFilters.sourcesFilters,
            collectionId: taggedByCuralateStream.id,
            contentGroup: 'Nothing',
            moderationStatus: CollectionStatusType.Unmoderated,
            limit: ITEMS_TO_RENDER,
            offset: 0,
          });

        let taggedByCuralatePendingPreviewItems: IContentLibraryExtendedItem[] =
          taggedByCuralateResp?.contentItems ?? [];
        const taggedByCuralatePendingCount =
          taggedByCuralateResp?.totalCount ?? 0;

        let itemDetailsByPartialCuid: ReturnType<
          typeof contentLibraryApiService.getItemDetailsAsync
        > = {};

        const cuidsNeedingDetails = taggedByCuralatePendingPreviewItems.map(
          (item) => Cuid.build(item, clientId),
        );

        try {
          itemDetailsByPartialCuid =
            await contentLibraryApiService.getItemDetailsAsync(
              cuidsNeedingDetails,
            );
        } catch (e) {
          // If the item details request fails, swallow the error and log it. We use these
          // details for some non-critical metadata overlays.
          crl8.logger?.cloud?.error?.(
            'Failed to load item details for the streams drawer',
            e,
          );
        }

        taggedByCuralatePendingPreviewItems =
          taggedByCuralatePendingPreviewItems.map(
            (item: IContentLibraryExtendedItem) => {
              const partialCuid = PartialCuid.build(item);
              return {
                ...item,
                itemDetails:
                  item.itemDetails || itemDetailsByPartialCuid[partialCuid],
              };
            },
          );

        dispatch<ISetTaggedByCuralatePendingCountAction>({
          type: StreamsDrawerActionNames.SET_TAGGED_BY_CURALATE_PENDING_COUNT,
          taggedByCuralatePendingCount,
          taggedByCuralatePendingPreviewItems,
        });
      }
    },

  checkContentDiscoveryFlag:
    () => (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      // The Content Discovery feature shows/hides the Recommended Streams UI in
      // the left sidebar. The ai_content_discovery flag is enabled when we create AI
      // recommended streams, and the tagged_by_curalate/connect flag is enabled when we
      // create the Tagged by Curalate/Connect stream. If either flag is enabled, we'll
      // show the sidebar.
      const isAiContentDiscoveryEnabled =
        !!getState().session.features[FeatureNames.AiContentDiscovery];
      const isTaggedByCuralateEnabled =
        !!getState().session.features[FeatureNames.TaggedByCuralate];
      const isConnectStreamEnabled =
        !!getState().session.features[FeatureNames.ConnectStream];
      dispatch(
        setContentDiscoveryFlag(
          isAiContentDiscoveryEnabled ||
            isTaggedByCuralateEnabled ||
            isConnectStreamEnabled,
        ),
      );
    },

  fetchRecommendationsStreams:
    () => async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      const isContentDiscoveryEnabled =
        getState().streams.streamsDrawer.isContentDiscoveryEnabled;

      if (isContentDiscoveryEnabled) {
        const recommendationsStreams = (await collectionsApi.getStreams(
          CollectionType.RECOMMENDATIONS_STREAM,
        )) as IStream[];

        // Always show the "Tagged by Curalate" and "Connect" streams at the top if they exist. Then
        // alphabetize the remaining streams by name.
        const orderedRecommendationStream = _.sortBy(
          recommendationsStreams,
          (stream) => {
            const isTaggedByCuralateStream =
              nameToStreamType(stream) ===
              StreamsDrawerStreamType.TAGGED_BY_CURALTE;
            const isConnectStream =
              nameToStreamType(stream) === StreamsDrawerStreamType.CONNECT;
            if (isTaggedByCuralateStream) {
              return 0;
            } else if (isConnectStream) {
              return 1;
            } else {
              return stream.name.toLowerCase().charCodeAt(0);
            }
          },
        );
        dispatch(setRecommendationsStreams(orderedRecommendationStream));

        // If the user has a Moderation Services "Tagged by Curalate"
        // stream or a "Connect" stream, fetch the number of pending items so we can show an
        // indicator in the UI and a preview
        dispatch(actions.fetchTaggedByCuralatePendingCount());
        dispatch(actions.fetchConnectPendingCount());
      }
    },

  fetchConnectPendingCount:
    () => async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      const state: IStreamsDrawerState = getState().streams.streamsDrawer;
      const { recommendationsStreams } = state;

      const collectionFiltersConverter = getCollectionFiltersConverter();
      const contentLibraryApiService = getContentLibraryApiService();

      // If the user has a "Connect" stream, fetch the number of pending items so
      // we can show an indicator in the UI.
      const connectStream = recommendationsStreams.find(
        (recStream) =>
          nameToStreamType(recStream) === StreamsDrawerStreamType.CONNECT,
      );

      if (connectStream) {
        const connectFilters =
          collectionFiltersConverter.collectionToFilters(connectStream);
        const connectResp = await contentLibraryApiService.getContentItemsAsync(
          {
            ...connectFilters.sourcesFilters,
            collectionId: connectStream.id,
            contentGroup: 'Nothing',
            moderationStatus: CollectionStatusType.Unmoderated,
            limit: 0,
            offset: 0,
          },
        );

        const connectPendingCount = connectResp?.totalCount ?? 0;
        dispatch<ISetConnectPendingCountAction>({
          type: StreamsDrawerActionNames.SET_CONNECT_PENDING_COUNT,
          connectPendingCount,
        });
      }
    },

  fetchBrandInstagram: () => async (dispatch: Redux.Dispatch) => {
    dispatch<IRequestBrandInstagramAction>({
      type: StreamsDrawerActionNames.REQUEST_BRAND_INSTAGRAM,
    });

    try {
      const instagramSettings =
        await instagramSettingsApi.getInstagramSettings();

      const { brandInstagramId, brandInstagramUsername } =
        getInstagramSettings(instagramSettings);

      const primaryAccountId = instagramSettings.primaryAccountId;
      const primaryAccount = instagramSettings.accounts.find(
        (account) => account.accountId === primaryAccountId,
      );
      const hasPrimaryAccountConnected =
        primaryAccount && !!primaryAccount.accountId;
      dispatch(
        assetFeedsModalActions.setAssetFeedSourceConnectionState(
          MEDIA_SOURCE.INSTAGRAM,
          !!hasPrimaryAccountConnected,
        ),
      );

      dispatch<IReceiveBrandInstagramSucceededAction>({
        type: StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_SUCCEEDED,
        brandInstagramId,
        brandInstagramUsername,
      });
    } catch {
      dispatch<IReceiveBrandInstagramFailedAction>({
        type: StreamsDrawerActionNames.RECEIVE_BRAND_INSTAGRAM_FAILED,
      });
    }
  },

  fetchClientRejectionReasons:
    () => async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      dispatch<IRequestClientRejectionReasonsAction>({
        type: StreamsDrawerActionNames.REQUEST_CLIENT_REJECTION_REASONS,
      });

      const clientId = ClientId.fromString(getState().session.client.id);

      try {
        const clientRejectionReasonsResponse =
          await shareApi.getClientRejectionReasons(clientId);
        const clientRejectionReasons = clientRejectionReasonsResponse.reasons;

        dispatch<IReceiveClientRejectionReasonsSucceededAction>({
          type: StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_SUCCEEDED,
          clientRejectionReasons,
        });
      } catch {
        dispatch<IReceiveClientRejectionReasonsFailedAction>({
          type: StreamsDrawerActionNames.RECEIVE_CLIENT_REJECTION_REASONS_FAILED,
        });
      }
    },

  /* Uncomment if we add facebook as a source
  fetchBrandFacebook: () => async (dispatch: Redux.Dispatch) => {
    dispatch<IRequestBrandFacebookAction>({
      type: StreamsDrawerActionNames.REQUEST_BRAND_FACEBOOK,
    });

    try {
      const { connectedFacebookPage, isConnected } = await facebookSettingsApi.getFacebookSettings();
      const pageName = connectedFacebookPage.pageName;

      const hasFacebookAccountConnected = isConnected && !!connectedFacebookPage;
      dispatch(assetFeedsModalActions.setAssetFeedSourceConnectionState(
        MEDIA_SOURCE.FACEBOOK,
        hasFacebookAccountConnected,
      ));

      dispatch<IReceiveBrandFacebookSucceededAction>({
        type: StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_SUCCEEDED,
        pageName,
      });
    } catch {
      dispatch<IReceiveBrandFacebookFailedAction>({
        type: StreamsDrawerActionNames.RECEIVE_BRAND_FACEBOOK_FAILED,
      });
    }
  },
  */

  openStreamsDrawer:
    () => async (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<IOpenStreamsDrawerAction>({
        type: StreamsDrawerActionNames.OPEN_STREAMS_DRAWER,
      });

      setTrackingSourceTimeout(dispatch);
    },

  openTaggedByCuralateStream:
    () =>
    async (
      dispatch: Redux.Dispatch<StreamDrawerActionType>,
      getState: () => ICuralateStore,
    ) => {
      const recommendationsStreams: IStream[] =
        getState().streams.streamsDrawer.recommendationsStreams;
      const taggedByCuralateStream = recommendationsStreams?.find(
        (stream) =>
          nameToStreamType(stream) ===
          StreamsDrawerStreamType.TAGGED_BY_CURALTE,
      );

      if (taggedByCuralateStream) {
        dispatch(
          getStreamsDrawerActions().selectStream(taggedByCuralateStream),
        );
        dispatch<IOpenStreamsDrawerAction>({
          type: StreamsDrawerActionNames.OPEN_STREAMS_DRAWER,
        });
      }
    },

  removeItemInPlace:
    (itemIdToRemove: string) =>
    (
      dispatch: Redux.Dispatch<IUpdateItemsInPlaceAction>,
      getState: () => ICuralateStore,
    ) => {
      const { items, totalItemCount } = getState().streams.streamsDrawer;
      dispatch<IUpdateItemsInPlaceAction>({
        type: StreamsDrawerActionNames.UPDATE_ITEMS_IN_PLACE,
        items: items.filter((item) => item.id !== itemIdToRemove),
        totalItemCount: Math.max(totalItemCount - 1, 0),
      });
    },

  updateItemWithUiaUpdates:
    (itemIdToUpdate: string, response: IUiaModalClosedResponse) =>
    (
      dispatch: Redux.Dispatch<IUpdateItemsInPlaceAction>,
      getState: () => ICuralateStore,
    ) => {
      const { items, totalItemCount } = getState().streams.streamsDrawer;
      const itemsWithUpdatedItem: IContentLibraryExtendedItem[] = (
        items as IContentLibraryExtendedItem[]
      ).map((item) => {
        if (item.id === itemIdToUpdate) {
          return {
            ...item,
            products: response.products,
            labels: toLabelsString(response.userTags), // Labels are stored as a single string
          };
        } else {
          return item;
        }
      });
      dispatch<IUpdateItemsInPlaceAction>({
        type: StreamsDrawerActionNames.UPDATE_ITEMS_IN_PLACE,
        items: itemsWithUpdatedItem,
        totalItemCount,
      });
    },

  fetchNextContentItems:
    (stream: IStream) =>
    (
      dispatch: Redux.Dispatch<IUpdateItemsInPlaceAction>,
      getState: () => ICuralateStore,
    ) => {
      const { filters } = getState().streams.streamsDrawer;
      dispatch(
        getStreamsDrawerActions().fetchNextContentItems(stream, filters),
      );
    },

  openAutoApprovalEntryModal:
    () => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<IOpenAutoApprovalEntryModal>({
        type: StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_ENTRY_MODAL,
      });
    },

  closeAutoApprovalEntryModal:
    () => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<ICloseAutoApprovalEntryModal>({
        type: StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_ENTRY_MODAL,
      });
    },

  openAutoApprovalModal:
    () => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<IOpenAutoApprovalModal>({
        type: StreamsDrawerActionNames.OPEN_AUTO_APPROVAL_MODAL,
      });
    },

  closeAutoApprovalModal:
    () => (dispatch: Redux.Dispatch<StreamDrawerActionType>) => {
      dispatch<ICloseAutoApprovalModal>({
        type: StreamsDrawerActionNames.CLOSE_AUTO_APPROVAL_MODAL,
      });
    },

  openCreateApprovalModal:
    () => async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
      const configs = await dispatch(
        actions.fetchTaggedByBVAutomationConfigs(),
      );
      configs && configs?.length > 0
        ? dispatch(actions.openAutoApprovalModal())
        : dispatch(actions.openAutoApprovalEntryModal());
    },

  fetchTaggedByBVAutomationConfigs: createThunk(
    () => async (dispatch, getState) => {
      const clientId = ClientId.fromString(getState().session.client.id);

      try {
        const clientTaggedByBvAutomationResponse =
          await taggedByBVAutomationApi.getClientTaggedByBazaarvoiceConfigurations(
            clientId,
          );
        const taggedByBvAutomationConfiguration =
          clientTaggedByBvAutomationResponse.configurations;

        dispatch(
          actions.updateActiveTaggedByBVAutomationConfig(
            taggedByBvAutomationConfiguration,
          ),
        );
        dispatch<IReceiveclientTaggedByBvAutomationResponseSucceededAction>({
          type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_SUCCEEDED,
          taggedByBvAutomationConfiguration,
        });
        return taggedByBvAutomationConfiguration;
      } catch {
        dispatch<IReceiveclientTaggedByBvAutomationResponseFailedAction>({
          type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_AUTOMATION_CONFIGURATION_FAILED,
        });
      }
    },
  ),

  updateActiveTaggedByBVAutomationConfig: createThunk(
    (taggedByBvAutomationConfiguration: IConfiguration[]) =>
      (dispatch: Redux.Dispatch) => {
        let taggedByBvAutomationActiveConfiguration =
          taggedByBvAutomationConfiguration.find((obj) => {
            return obj.isActive === true;
          });
        if (taggedByBvAutomationActiveConfiguration) {
          dispatch<IReceiveclientTaggedByBvActiveAutomationResponseSucceededAction>(
            {
              type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_AUTOMATION_CONFIGURATION_SUCCEEDED,
              taggedByBvAutomationActiveConfiguration,
            },
          );
        } else {
          dispatch<IReceiveclientTaggedByBvNoActiveAutomationData>({
            type: StreamsDrawerActionNames.RECEIVE_CLIENT_TBB_ACTIVE_NO_AUTOMATION_CONFIGURATION,
          });
        }
      },
  ),

  fetchTaggedByBVAutomationActiveConfigs: createThunk(
    () => async (dispatch, getState: () => ICuralateStore) => {
      const clientId = ClientId.fromString(getState().session.client.id);

      try {
        const clientTaggedByBvActiveAutomationResponse =
          await taggedByBVAutomationApi.getClientTaggedByBazaarvoiceActiveConfigurations(
            clientId,
          );
        const taggedByBvAutomationActiveConfiguration =
          clientTaggedByBvActiveAutomationResponse.configurations;
        dispatch(
          actions.updateActiveTaggedByBVAutomationConfig(
            taggedByBvAutomationActiveConfiguration,
          ),
        );
        return taggedByBvAutomationActiveConfiguration;
      } catch (e) {
        // If the active configuration request fails, swallow the error and log it. We use these
        // details for some non-critical metadata overlays.
        crl8.logger?.cloud?.error?.(
          'Failed to load active configuration for the streams drawer',
          e,
        );
      }
    },
  ),

  deleteTaggedByBVAutomationConfig: createThunk(
    (configId: number) =>
      async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
        const clientId = ClientId.fromString(getState().session.client.id);

        try {
          await taggedByBVAutomationApi.deleteConfiguration(clientId, configId);

          //refersh the configuration
          const configs = await dispatch(
            actions.fetchTaggedByBVAutomationConfigs(),
          );

          configs && configs?.length === 0
            ? dispatch(actions.closeAutoApprovalModal())
            : dispatch(actions.openAutoApprovalEntryModal());

          dispatch<IDeleteTaggedByBvAutomationSuccess>({
            type: StreamsDrawerActionNames.DELETE_TBB_AUTOMATION_CONFIGURATION_SUCCEEDED,
          });
        } catch {
          dispatch<IDeleteTaggedByBvAutomationFailed>({
            type: StreamsDrawerActionNames.DELETE_TBB_AUTOMATION_CONFIGURATION_FAILED,
          });
        }
      },
  ),

  saveTaggedByBVAutomationConfig: createThunk(
    (configuration: IConfiguration) =>
      async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
        const clientId = ClientId.fromString(getState().session.client.id);

        try {
          await taggedByBVAutomationApi.saveConfiguration(
            clientId,
            configuration,
          );

          //refersh the configuration
          const configs = await dispatch(
            actions.fetchTaggedByBVAutomationConfigs(),
          );
        } catch (e) {
          // If the save configuration request fails, swallow the error and log it. We use these
          // details for some non-critical metadata overlays.
          crl8.logger?.cloud?.error?.(
            'Failed to save the configurations for tagged by Bazaarvoice',
            e,
          );
        }
      },
  ),

  updateTaggedByBVAutomationConfig: createThunk(
    (configuration: IConfiguration[]) =>
      async (dispatch: Redux.Dispatch, getState: () => ICuralateStore) => {
        const clientId = ClientId.fromString(getState().session.client.id);

        try {
          await taggedByBVAutomationApi.updateConfiguration(
            clientId,
            configuration,
          );

          //refersh the configuration
          const configs = await dispatch(
            actions.fetchTaggedByBVAutomationConfigs(),
          );
        } catch (e) {
          // If the update configuration request fails, swallow the error and log it. We use these
          // details for some non-critical metadata overlays.
          crl8.logger?.cloud?.error?.(
            'Failed to update the configurations for tagged by Bazaarvoice',
            e,
          );
        }
      },
  ),
  selectGallery: (reelId: ReelId) => {
    return createAction({
      type: StreamsDrawerActionNames.ADD_MODAL_SELECT_GALLERY,
      reelId,
    });
  },
  deselectGallery: (reelId: ReelId) => {
    return createAction({
      type: StreamsDrawerActionNames.ADD_MODAL_DESELECT_GALLERY,
      reelId,
    });
  },
};
